import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { Trash2, RotateCcw, Pencil, UserMinus } from 'lucide-react';
import {
  useHome,
  useRenameHome,
  useRemoveHomeMember,
  useRotateInviteCode,
  useDeleteHome,
} from '@/features/homes/api';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Dialog } from '@/components/ui/Dialog';

export const Route = createFileRoute('/_authed/homes/$id')({
  component: HomeDetailPage,
});

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toISOString().slice(0, 10);
}

// ── Confirm dialog (shared by rotate, remove-member, delete) ──────────────────

type ConfirmAction =
  | { kind: 'rotate' }
  | { kind: 'remove-member'; userId: string; displayName: string }
  | { kind: 'delete' };

interface ConfirmDialogProps {
  action: ConfirmAction;
  homeId: string;
  onClose: () => void;
  onMemberRemoved?: (userId: string) => void;
  onDeleted?: () => void;
  onCodeRotated?: (code: string) => void;
}

function ConfirmDialog({
  action,
  homeId,
  onClose,
  onMemberRemoved,
  onDeleted,
  onCodeRotated,
}: ConfirmDialogProps) {
  const removeMember = useRemoveHomeMember();
  const rotateCode = useRotateInviteCode();
  const deleteHome = useDeleteHome();
  const [error, setError] = useState<string | null>(null);

  const isPending =
    removeMember.isPending || rotateCode.isPending || deleteHome.isPending;

  let title = '';
  let body = '';
  if (action.kind === 'rotate') {
    title = 'Rotate invite code?';
    body = 'The current code will be invalidated and a new one generated.';
  } else if (action.kind === 'remove-member') {
    title = 'Remove member?';
    body = `Remove ${action.displayName} from this home?`;
  } else {
    title = 'Delete home?';
    body = 'This will permanently delete the home and remove all members.';
  }

  const handleConfirm = async () => {
    setError(null);
    try {
      if (action.kind === 'rotate') {
        const result = await rotateCode.mutateAsync(homeId);
        onCodeRotated?.(result.code);
        onClose();
      } else if (action.kind === 'remove-member') {
        await removeMember.mutateAsync({ homeId, userId: action.userId });
        onMemberRemoved?.(action.userId);
        onClose();
      } else {
        await deleteHome.mutateAsync(homeId);
        onDeleted?.();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
    }
  };

  return (
    <Dialog open testId="home-confirm-dialog" title={title} onClose={onClose}>
      <div className="flex flex-col gap-4">
        <p className="text-sm text-ink-soft">{body}</p>

        {error && (
          <div
            className="text-xs rounded-lg p-3"
            style={{
              background: 'var(--color-error-bg)',
              border: '1px solid var(--color-error-border)',
              color: 'var(--color-error-text)',
            }}
          >
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            data-testid="home-confirm-button"
            onClick={handleConfirm}
            disabled={isPending}
          >
            {isPending ? 'Working…' : 'Confirm'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ── Rename dialog ─────────────────────────────────────────────────────────────

interface RenameDialogProps {
  homeId: string;
  currentName: string;
  onClose: () => void;
}

function RenameDialog({ homeId, currentName, onClose }: RenameDialogProps) {
  const rename = useRenameHome();
  const [name, setName] = useState(currentName);
  const [error, setError] = useState<string | null>(null);

  const isEmpty = name.trim().length === 0;

  const handleSubmit = async () => {
    if (isEmpty) return;
    setError(null);
    try {
      await rename.mutateAsync({ id: homeId, name: name.trim() });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Rename failed');
    }
  };

  return (
    <Dialog open testId="home-rename-dialog" title="Rename home" onClose={onClose}>
      <div className="flex flex-col gap-4">
        <Input
          label="Home name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="home-rename-input"
          onKeyDown={(e) => {
            if (e.key === 'Enter') void handleSubmit();
          }}
        />

        {error && (
          <div
            className="text-xs rounded-lg p-3"
            style={{
              background: 'var(--color-error-bg)',
              border: '1px solid var(--color-error-border)',
              color: 'var(--color-error-text)',
            }}
          >
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={rename.isPending}>
            Cancel
          </Button>
          <Button
            data-testid="home-rename-submit"
            onClick={handleSubmit}
            disabled={rename.isPending || isEmpty}
          >
            {rename.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

function HomeDetailPage() {
  const { id } = Route.useParams();
  const { data: home, isLoading, error } = useHome(id);
  const navigate = useNavigate();

  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [renameOpen, setRenameOpen] = useState(false);

  if (isLoading) {
    return <div className="p-8 text-ink-soft">Loading…</div>;
  }

  if (error) {
    return (
      <div className="p-8 text-ink-soft">
        Failed to load home{error instanceof Error ? `: ${error.message}` : '.'}{' '}
        <Link to="/homes" className="text-honey underline">
          Back to list
        </Link>
      </div>
    );
  }

  if (!home) {
    return (
      <div className="p-8 text-ink-soft">
        Home not found.{' '}
        <Link to="/homes" className="text-honey underline">
          Back to list
        </Link>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 flex flex-col gap-6">
      {/* Header */}
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Link to="/homes" className="text-xs text-ink-faint hover:text-ink mb-1 block">
            ← Homes
          </Link>
          <h1
            className="font-serif text-3xl leading-tight"
            data-testid="home-name"
          >
            {home.name}
          </h1>
          <div className="font-mono text-xs text-ink-faint mt-1 break-all">{home.id}</div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="ghost"
            size="sm"
            data-testid="home-rename-button"
            onClick={() => setRenameOpen(true)}
          >
            <Pencil size={14} className="mr-1" />
            Rename
          </Button>
          <Button
            variant="ghost"
            size="sm"
            data-testid="home-rotate-code-button"
            onClick={() => setConfirmAction({ kind: 'rotate' })}
          >
            <RotateCcw size={14} className="mr-1" />
            Rotate code
          </Button>
          <Button
            variant="ghost"
            size="sm"
            data-testid="home-delete-button"
            onClick={() => setConfirmAction({ kind: 'delete' })}
          >
            <Trash2 size={14} className="mr-1" />
            Delete
          </Button>
        </div>
      </header>

      {/* Meta card */}
      <Card shadow="sm" className="p-4 sm:p-6 flex flex-col gap-4">
        <h2 className="font-serif text-xl">Details</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="text-xs text-ink-faint uppercase tracking-wide mb-1">Invite code</div>
            <div
              className="font-mono font-medium"
              data-testid="home-invite-code"
            >
              {home.inviteCode ?? 'No active code'}
            </div>
          </div>
          <div>
            <div className="text-xs text-ink-faint uppercase tracking-wide mb-1">Code expires</div>
            <div className="font-mono text-ink-soft">
              {formatDate(home.inviteCodeExpiresAt)}
            </div>
          </div>
          <div>
            <div className="text-xs text-ink-faint uppercase tracking-wide mb-1">Created</div>
            <div className="font-mono text-ink-soft">{formatDate(home.createdAt)}</div>
          </div>
        </div>
      </Card>

      {/* Members */}
      <Card shadow="sm" className="p-0 overflow-hidden">
        <div className="px-4 py-4 sm:px-6 border-b flex items-center justify-between gap-2" style={{ borderColor: 'var(--color-hairline)' }}>
          <h2 className="font-serif text-xl">Members</h2>
          <Badge tone="neutral">{home.members.length}</Badge>
        </div>
        {home.members.length === 0 ? (
          <div className="p-6 text-ink-soft text-sm">No members.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr
                className="text-left text-xs uppercase tracking-wide text-ink-faint border-b"
                style={{ borderColor: 'var(--color-hairline)' }}
              >
                <th className="px-4 py-3">Member</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Joined</th>
                <th className="px-4 py-3 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {home.members.map((m) => (
                <tr
                  key={m.userId}
                  data-testid={`home-member-row-${m.userId}`}
                  className="border-b last:border-b-0"
                  style={{ borderColor: 'var(--color-hairline)' }}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium">{m.displayName}</div>
                    <div className="font-mono text-xs text-ink-faint break-all">{m.userId}</div>
                  </td>
                  <td className="px-4 py-3 text-ink-soft">{m.email}</td>
                  <td className="px-4 py-3 text-ink-soft font-mono text-xs">
                    {formatDate(m.joinedAt)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid={`home-member-remove-${m.userId}`}
                      aria-label={`Remove ${m.displayName} from home`}
                      onClick={() =>
                        setConfirmAction({
                          kind: 'remove-member',
                          userId: m.userId,
                          displayName: m.displayName,
                        })
                      }
                    >
                      <UserMinus size={14} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Shared recipes */}
      <Card shadow="sm" className="p-0 overflow-hidden">
        <div className="px-4 py-4 sm:px-6 border-b flex items-center justify-between gap-2" style={{ borderColor: 'var(--color-hairline)' }}>
          <h2 className="font-serif text-xl">Shared recipes</h2>
          <Badge tone="neutral" data-testid="home-shared-count">
            {home.sharedRecipeCount}
          </Badge>
        </div>
        {home.sharedRecipes.length === 0 ? (
          <div className="p-6 text-ink-soft text-sm">No shared recipes.</div>
        ) : (
          <ul className="divide-y" style={{ borderColor: 'var(--color-hairline)' }}>
            {home.sharedRecipes.map((r) => (
              <li key={r.recipeId} className="px-4 py-3">
                <Link
                  to="/recipes/$id/edit"
                  params={{ id: r.recipeId }}
                  data-testid={`home-shared-recipe-${r.recipeId}`}
                  className="font-medium hover:underline"
                >
                  {r.title}
                </Link>
                <div className="font-mono text-xs text-ink-faint break-all">{r.recipeId}</div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Cook list */}
      <Card shadow="sm" className="p-0 overflow-hidden">
        <div className="px-4 py-4 sm:px-6 border-b flex items-center justify-between gap-2" style={{ borderColor: 'var(--color-hairline)' }}>
          <h2 className="font-serif text-xl">Cook list</h2>
          <Badge tone="neutral" data-testid="home-cooklist-count">
            {home.cookListCount}
          </Badge>
        </div>
        {home.cookList.length === 0 ? (
          <div className="p-6 text-ink-soft text-sm">Cook list is empty.</div>
        ) : (
          <ul className="divide-y" style={{ borderColor: 'var(--color-hairline)' }}>
            {home.cookList.map((entry) => (
              <li key={entry.id} className="px-4 py-3">
                <div className="font-medium">{entry.title}</div>
                <div className="font-mono text-xs text-ink-faint">
                  Added {formatDate(entry.addedAt)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Confirm dialog (rotate / remove-member / delete) */}
      {confirmAction && (
        <ConfirmDialog
          action={confirmAction}
          homeId={id}
          onClose={() => {
            setConfirmAction(null);
          }}
          onMemberRemoved={() => {
            setConfirmAction(null);
          }}
          onCodeRotated={() => {
            setConfirmAction(null);
          }}
          onDeleted={() => {
            void navigate({ to: '/homes' });
          }}
        />
      )}

      {/* Rename dialog */}
      {renameOpen && (
        <RenameDialog
          homeId={id}
          currentName={home.name}
          onClose={() => setRenameOpen(false)}
        />
      )}
    </div>
  );
}
